import React, { createContext, useContext, useState, useCallback } from "react"

interface FloatingMenuContextValue {
  // Grug menu (FAB)
  isGrugMenuOpen: boolean
  openGrugMenu: () => void
  closeGrugMenu: () => void
  toggleGrugMenu: () => void
  // Profile edit modal
  isProfileEditModalOpen: boolean
  openProfileEditModal: () => void
  closeProfileEditModal: () => void
}

const defaultValue: FloatingMenuContextValue = {
  isGrugMenuOpen: false,
  openGrugMenu: () => {},
  closeGrugMenu: () => {},
  toggleGrugMenu: () => {},
  isProfileEditModalOpen: false,
  openProfileEditModal: () => {},
  closeProfileEditModal: () => {},
}

export const FloatingMenuContext = createContext<FloatingMenuContextValue>(defaultValue)

export function useFloatingMenu() {
  const context = useContext(FloatingMenuContext)
  if (!context) {
    throw new Error("useFloatingMenu must be used within a FloatingMenuProvider")
  }
  return context
}

interface FloatingMenuContextProviderProps {
  children: React.ReactNode
}

export function FloatingMenuContextProvider({
  children,
}: FloatingMenuContextProviderProps) {
  const [isGrugMenuOpen, setIsGrugMenuOpen] = useState(false)
  const [isProfileEditModalOpen, setIsProfileEditModalOpen] = useState(false)

  const openGrugMenu = useCallback(() => {
    setIsGrugMenuOpen(true)
  }, [])

  const closeGrugMenu = useCallback(() => {
    setIsGrugMenuOpen(false)
  }, [])

  const toggleGrugMenu = useCallback(() => {
    setIsGrugMenuOpen((prev) => !prev)
  }, [])

  const openProfileEditModal = useCallback(() => {
    setIsProfileEditModalOpen(true)
  }, [])

  const closeProfileEditModal = useCallback(() => {
    setIsProfileEditModalOpen(false)
  }, [])

  const value: FloatingMenuContextValue = {
    isGrugMenuOpen,
    openGrugMenu,
    closeGrugMenu,
    toggleGrugMenu,
    isProfileEditModalOpen,
    openProfileEditModal,
    closeProfileEditModal,
  }

  return (
    <FloatingMenuContext.Provider value={value}>
      {children}
    </FloatingMenuContext.Provider>
  )
}

export default FloatingMenuContextProvider
